import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { chatAPI } from '../services/api';
import { 
  MessageCircle, 
  Plus, 
  Trash2, 
  User, 
  LogOut, 
  X,
  Bot,
  Sparkles,
  Brain,
  Zap
} from 'lucide-react';

const AI_MODEL_ICONS = {
  chatgpt: { icon: Bot, color: 'text-green-600', bg: 'bg-green-50' },
  gemini: { icon: Sparkles, color: 'text-blue-600', bg: 'bg-blue-50' },
  claude: { icon: Brain, color: 'text-purple-600', bg: 'bg-purple-50' },
  deepseek: { icon: Zap, color: 'text-orange-600', bg: 'bg-orange-50' }
};

const Sidebar = ({ isOpen, onClose, onNewChat, selectedChatId, onChatSelect }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [chatHistory, setChatHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (location.pathname === '/chat') {
      fetchChatHistory();
    }
  }, [location.pathname]);

  const fetchChatHistory = async () => {
    try {
      setLoading(true);
      const response = await chatAPI.getChatHistory();
      if (response.data.success) {
        setChatHistory(response.data.chats);
      }
    } catch (error) {
      console.error('Error fetching chat history:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteChat = async (chatId, e) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this chat?')) {
      try {
        await chatAPI.deleteChat(chatId);
        setChatHistory(prev => prev.filter(chat => chat._id !== chatId));
        if (selectedChatId === chatId) {
          onNewChat();
        }
      } catch (error) {
        console.error('Error deleting chat:', error);
      }
    }
  };

  const handleLogout = () => {
    logout();
    onClose();
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return 'Today';
    if (diffDays === 2) return 'Yesterday';
    if (diffDays <= 7) return `${diffDays - 1} days ago`;
    return date.toLocaleDateString();
  };

  const sidebarContent = (
    <div className="flex flex-col h-full bg-white border-r border-gray-200">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 bg-gradient-to-r from-primary-500 to-primary-600 rounded-lg flex items-center justify-center">
            <span className="text-white font-bold text-sm">AI</span>
          </div>
          <span className="font-bold text-gray-900 hidden sm:block">Fusion Chat</span>
        </div>
        <button
          onClick={onClose}
          className="lg:hidden p-1 hover:bg-gray-100 rounded-md transition-colors"
        >
          <X className="w-5 h-5 text-gray-500" />
        </button>
      </div>

      {/* New Chat Button */}
      <div className="p-4">
        <button
          onClick={() => {
            onNewChat();
            onClose();
          }}
          className="w-full flex items-center justify-center space-x-2 bg-primary-600 hover:bg-primary-700 text-white font-medium py-3 px-4 rounded-lg transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span>New Chat</span>
        </button>
      </div>

      {/* Chat History */}
      <div className="flex-1 overflow-y-auto px-4">
        <div className="mb-4">
          <h3 className="text-sm font-medium text-gray-500 mb-2">Recent Chats</h3>
          {loading ? (
            <div className="space-y-2">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-12 bg-gray-100 rounded-lg animate-pulse"></div>
              ))}
            </div>
          ) : chatHistory.length > 0 ? (
            <div className="space-y-1">
              {chatHistory.map((chat) => {
                const modelInfo = AI_MODEL_ICONS[chat.model] || AI_MODEL_ICONS.chatgpt;
                const ModelIcon = modelInfo.icon;
                const isSelected = selectedChatId === chat._id;
                
                return (
                  <div
                    key={chat._id}
                    onClick={() => {
                      onChatSelect(chat._id);
                      onClose();
                    }}
                    className={`group flex items-center space-x-3 p-3 rounded-lg cursor-pointer transition-colors ${
                      isSelected 
                        ? 'bg-primary-50 border border-primary-200' 
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${modelInfo.bg}`}>
                      <ModelIcon className={`w-4 h-4 ${modelInfo.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium truncate ${
                        isSelected ? 'text-primary-900' : 'text-gray-900'
                      }`}>
                        {chat.title}
                      </p>
                      <p className={`text-xs truncate ${
                        isSelected ? 'text-primary-600' : 'text-gray-500'
                      }`}>
                        {formatDate(chat.updatedAt)}
                      </p>
                    </div>
                    <button
                      onClick={(e) => handleDeleteChat(chat._id, e)}
                      className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-100 rounded transition-all"
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </button>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              <MessageCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500">No chats yet</p>
              <p className="text-xs text-gray-400">Start a new conversation</p>
            </div>
          )}
        </div>
      </div>

      {/* User Menu */}
      <div className="border-t border-gray-200 p-4">
        <div className="space-y-2">
          <Link
            to="/profile"
            onClick={onClose}
            className="flex items-center space-x-3 p-2 hover:bg-gray-50 rounded-lg transition-colors"
          >
            <User className="w-4 h-4 text-gray-500" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{user?.name}</p>
              <p className="text-xs text-gray-500">{user?.tokens || 0} tokens</p>
            </div>
          </Link>
          <button
            onClick={handleLogout}
            className="w-full flex items-center space-x-3 p-2 hover:bg-red-50 rounded-lg transition-colors text-red-600"
          >
            <LogOut className="w-4 h-4" />
            <span className="text-sm font-medium">Logout</span>
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <div className="hidden lg:block w-80 h-full">
        {sidebarContent}
      </div>

      {/* Mobile/Tablet Overlay */}
      {isOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
            onClick={onClose}
          />
          <div className="relative w-80 max-w-sm h-full transform transition-transform">
            {sidebarContent}
          </div>
        </div>
      )}
    </>
  );
};

export default Sidebar;
